import { fromEvent } from 'rxjs'
import { sound, type Feeling } from '@schmancy/audio'
import SchmancyNotification, { NotificationType } from './notification'

export interface NotificationOptions {
	id?: string
	title?: string
	message: string
	type?: NotificationType
	duration?: number
	closable?: boolean
	playSound?: boolean
	showProgress?: boolean
}

const typeToFeeling: Record<NotificationType, Feeling> = {
	info: 'curious',
	success: 'content',
	warning: 'anxious',
	error: 'disappointed',
}

const typeDurations: Record<NotificationType, number> = {
	success: 1500,
	info: 2000,
	warning: 2500,
	error: 2500,
}

// One shared notification channel dings for dozens of messages. Route it
// through `sound` (the theme-able service that carries the persisted, mutable
// schmancy/sound volume + mute) so the operator's mute reaches toasts too, and
// so an app that registers a dedicated failure cue plays it here.
function playNotificationSound(type: NotificationType): void {
	if (type === 'error' && sound.hasSample('error')) {
		sound.playCue('error')
		return
	}
	sound.play(typeToFeeling[type])
}

let lastClickPosition = { x: window.innerWidth - 100, y: 50 }
if (typeof window !== 'undefined') {
	fromEvent<MouseEvent>(window, 'mousedown', { capture: true, passive: true } as AddEventListenerOptions).subscribe(
		e => {
			lastClickPosition = { x: e.clientX, y: e.clientY }
		},
	)
}

let currentNotification: SchmancyNotification | null = null
const notificationStack: string[] = []

function show(options: NotificationOptions): string {
	const id = options.id ?? `notification-${Date.now()}-${Math.floor(Math.random() * 10000)}`
	notificationStack.push(id)

	if (currentNotification) {
		currentNotification.remove()
		currentNotification = null
	}

	const notification = document.createElement('sch-notification') as SchmancyNotification
	notification.id = id
	notification.title = options.title ?? ''
	notification.message = options.message
	notification.type = options.type ?? 'info'
	notification.duration = options.duration ?? 1000
	notification.closable = options.closable !== false
	notification.playSound = false
	notification.showProgress = options.showProgress ?? false
	notification.startPosition = { ...lastClickPosition }

	if (options.playSound !== false) {
		playNotificationSound(notification.type)
	}

	fromEvent(notification, 'close').subscribe(() => {
		const index = notificationStack.indexOf(id)
		if (index > -1) notificationStack.splice(index, 1)
		notification.remove()
		if (currentNotification === notification) currentNotification = null
	})

	document.body.appendChild(notification)
	currentNotification = notification
	return id
}

/** Fire-and-forget toast API; `show` is the low-level primitive the rest build on. */
export const $notify = {
	show,

	info: (message?: string, options: Partial<Omit<NotificationOptions, 'message' | 'type'>> = {}): string =>
		show({
			message: message ?? '',
			type: 'info',
			duration: message ? (options.duration ?? typeDurations.info) : 1,
			...options,
		}),

	success: (message?: string, options: Partial<Omit<NotificationOptions, 'message' | 'type'>> = {}): string =>
		show({
			message: message ?? '',
			type: 'success',
			duration: message ? (options.duration ?? typeDurations.success) : 1,
			...options,
		}),

	warning: (message?: string, options: Partial<Omit<NotificationOptions, 'message' | 'type'>> = {}): string =>
		show({
			message: message ?? '',
			type: 'warning',
			duration: message ? (options.duration ?? typeDurations.warning) : 1,
			...options,
		}),

	error: (message?: string, options: Partial<Omit<NotificationOptions, 'message' | 'type'>> = {}): string =>
		show({
			message: message ?? '',
			type: 'error',
			duration: message ? (options.duration ?? typeDurations.error) : 1,
			...options,
		}),

	customDuration: (
		message: string,
		duration: number,
		options: Partial<Omit<NotificationOptions, 'message' | 'duration'>> = {},
	): string => show({ message, duration, ...options }),

	persistent: (message: string, options: Partial<Omit<NotificationOptions, 'message' | 'duration'>> = {}): string =>
		show({ message, duration: 0, ...options }),

	dismiss: (id?: string): void => {
		const targetId = id ? notificationStack.splice(notificationStack.indexOf(id), 1)[0] : notificationStack.pop()
		if (targetId && currentNotification?.id === targetId) currentNotification.close()
	},

	update: (id: string, options: Partial<NotificationOptions>): void => {
		if (currentNotification?.id !== id) return
		if (options.title !== undefined) currentNotification.title = options.title
		if (options.message !== undefined) currentNotification.message = options.message
		if (options.type !== undefined) currentNotification.type = options.type
	},
}
